import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const MobileRegistration = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const preselectedEventId = searchParams.get('event');

  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: user?.email || '',
    phone: '',
    event: preselectedEventId || '',
    specialRequirements: '',
    agreeToTerms: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const { data, error } = await supabase
          .from('events')
          .select('id, title, start_date, registration_fee, is_paid_event')
          .in('status', ['upcoming', 'confirmed', 'ongoing'])
          .order('start_date', { ascending: true });
        if (error) throw error;
        setEvents(data || []);
      } catch (err) {
        console.error('Error fetching events:', err);
      } finally {
        setLoadingEvents(false);
      }
    };
    fetchEvents();
  }, []);

  useEffect(() => {
    if (user?.email && !formData.email) {
      setFormData(prev => ({ ...prev, email: user.email }));
    }
  }, [user]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      navigate(`/login?redirect=/register${preselectedEventId ? `?event=${preselectedEventId}` : ''}`);
    }
  }, [user, navigate, preselectedEventId]);

  useEffect(() => {
    if (preselectedEventId && events.length > 0) {
      setFormData(prev => ({ ...prev, event: preselectedEventId }));
    }
  }, [preselectedEventId, events]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const selectedEvent = events.find(e => e.id === formData.event);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const { data: existing } = await supabase
        .from('event_registrations')
        .select('id')
        .eq('event_id', formData.event)
        .eq('attendee_email', formData.email.trim().toLowerCase())
        .single();

      if (existing) {
        setError('You are already registered for this event');
        setIsSubmitting(false);
        return;
      }

      const { error: insertError } = await supabase
        .from('event_registrations')
        .insert({
          event_id: formData.event,
          user_id: user?.id || null,
          attendee_name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
          attendee_email: formData.email.trim().toLowerCase(),
          attendee_phone: formData.phone.trim() || null,
          notes: formData.specialRequirements.trim() || null,
          status: 'registered',
          payment_status: selectedEvent?.is_paid_event && selectedEvent?.registration_fee > 0 ? 'unpaid' : 'paid',
          amount_paid: 0,
        });

      if (insertError) throw insertError;

      try {
        await fetch('/.netlify/functions/send-registration-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attendeeName: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
            attendeeEmail: formData.email.trim().toLowerCase(),
            eventTitle: selectedEvent?.title,
            eventDate: selectedEvent?.start_date ? new Date(selectedEvent.start_date).toLocaleDateString() : null,
            eventLocation: null,
          }),
        });
      } catch (emailError) {
        console.error('Failed to send confirmation email:', emailError);
      }

      setSubmitSuccess(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      setError('Failed to register. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-dark via-primary to-primary-light flex flex-col items-center justify-center px-6">
        <div style={{ height: 'env(safe-area-inset-top)' }} />
        <div className="w-20 h-20 bg-white/15 backdrop-blur rounded-full flex items-center justify-center mb-6 ring-1 ring-white/20">
          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="font-display text-2xl font-bold text-white mb-2">Registration Successful!</h2>
        <p className="text-white/70 text-center mb-8 max-w-xs">
          We've sent a confirmation email to <strong>{formData.email}</strong>
        </p>
        <div className="w-full max-w-xs space-y-3">
          <button
            onClick={() => setSubmitSuccess(false)}
            className="w-full py-3.5 border border-white/30 text-white font-semibold rounded-xl active:scale-95 transition-transform"
          >
            Register Another Person
          </button>
          <button
            onClick={() => navigate('/')}
            className="w-full py-3.5 bg-white text-primary font-semibold rounded-xl active:scale-95 transition-transform"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-8">
      <div style={{ height: 'env(safe-area-inset-top)' }} />

      {/* Gradient header */}
      <div className="bg-gradient-to-br from-primary-dark to-primary px-5 pt-6 pb-8 rounded-b-3xl">
        <Link to="/" className="text-white/70 text-sm inline-flex items-center mb-3">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </Link>
        <h1 className="font-display text-2xl font-bold text-white">Event Registration</h1>
        <p className="text-white/60 text-sm mt-1">Register for an upcoming conference</p>
      </div>

      <div className="px-5 mt-6">
        {error && (
          <div className="mb-4 p-3.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-start">
            <svg className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-700 dark:text-red-300 text-sm">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Event Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Select Event</label>
            {loadingEvents ? (
              <div className="w-full px-4 py-3.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-400 text-sm">
                Loading events...
              </div>
            ) : (
              <select
                name="event"
                required
                value={formData.event}
                onChange={handleChange}
                className="w-full px-4 py-3.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white text-base focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Choose an event...</option>
                {events.map(event => (
                  <option key={event.id} value={event.id}>
                    {event.title}
                  </option>
                ))}
              </select>
            )}
            {selectedEvent?.is_paid_event && selectedEvent?.registration_fee > 0 && (
              <p className="mt-2 text-sm text-amber-600 dark:text-amber-400 font-medium">
                Registration fee: ${selectedEvent.registration_fee}
              </p>
            )}
          </div>

          {/* Name */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">First Name</label>
              <input
                name="firstName"
                type="text"
                required
                value={formData.firstName}
                onChange={handleChange}
                className="w-full px-4 py-3.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white text-base focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="John"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Last Name</label>
              <input
                name="lastName"
                type="text"
                required
                value={formData.lastName}
                onChange={handleChange}
                className="w-full px-4 py-3.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white text-base focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Smith"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Email</label>
            <input
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white text-base focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="you@example.com"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Phone (Optional)</label>
            <input
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-3.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white text-base focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="(555) 123-4567"
            />
          </div>

          {/* Special Requirements */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Special Requirements (Optional)</label>
            <textarea
              name="specialRequirements"
              value={formData.specialRequirements}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-3.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white text-base focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              placeholder="Dietary restrictions, accessibility needs, etc."
            />
          </div>

          {/* Terms */}
          <label className="flex items-start gap-2.5">
            <input
              type="checkbox"
              name="agreeToTerms"
              required
              checked={formData.agreeToTerms}
              onChange={handleChange}
              className="w-5 h-5 mt-0.5 text-primary border-gray-300 rounded flex-shrink-0"
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              I agree to the{' '}
              <Link to="/terms" className="text-primary font-medium">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-primary font-medium">Privacy Policy</Link>
            </span>
          </label>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3.5 bg-primary active:bg-primary-dark text-white font-semibold rounded-xl transition-all disabled:opacity-50 flex items-center justify-center min-h-[48px]"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Registering...
              </>
            ) : (
              'Complete Registration'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default MobileRegistration;
